import { tVector2 } from '../Utility/ArrayHelper';
import { RewardType } from '../Utility/Rewards/RewardType';

export interface IStarChestData {
    requiredStars: number;
    nodeChestStarsScale: tVector2;
    nodeChestStarsAnchors: [number, number, number, number];
    futureChestScale: tVector2;
    /** X and Y offsets specifying relative position between back and front parts of a chest */
    backFrontOffset: tVector2;
    /**
     * Array whose length specifies how many picks player get for given tier.
     * Elements of the array specify relative coordinates for turnips
     * when they are moved to the chest.
     */
    luckyTurnipPicks: ReadonlyArray<tVector2>;
    specialUpgradeSfx: boolean;
}

export const STAR_CHEST_TIER_DATA: IStarChestData[] = [
    {
        requiredStars: 0,
        nodeChestStarsScale: [0.65, 0.65],
        nodeChestStarsAnchors: [0.53, 0.46, 0.53, 0.46],
        futureChestScale: [0.5, 0.5],
        backFrontOffset: [0, -116],
        luckyTurnipPicks: [
            // 1 pick
            [0, 0],
        ].map(pos => mapTurnipOffset(pos, -35, 88)),
        specialUpgradeSfx: false,
    },
    {
        requiredStars: 25,
        nodeChestStarsScale: [0.65, 0.65],
        nodeChestStarsAnchors: [0.53, 0.46, 0.53, 0.46],
        futureChestScale: [0.48, 0.48],
        backFrontOffset: [-4, -128],
        luckyTurnipPicks: [
            // 2 picks
            [-0.5, 0],
            [0.5, 0],
        ].map(pos => mapTurnipOffset(pos, -35, 98, 75, 0, 12)),
        specialUpgradeSfx: false,
    },
    {
        requiredStars: 35,
        nodeChestStarsScale: [0.65, 0.65],
        nodeChestStarsAnchors: [0.53, 0.46, 0.53, 0.46],
        futureChestScale: [0.46, 0.46],
        backFrontOffset: [14, -153],
        luckyTurnipPicks: [
            // 3 picks
            [-1, 0],
            [0, 0],
            [1, 0],
        ].map(pos => mapTurnipOffset(pos, -35, 108, 50, 0, 12)),
        specialUpgradeSfx: false,
    },
    {
        requiredStars: 45,
        nodeChestStarsScale: [0.68, 0.68],
        nodeChestStarsAnchors: [0.53, 0.46, 0.53, 0.46],
        futureChestScale: [0.44, 0.44],
        backFrontOffset: [-1, -162],
        luckyTurnipPicks: [
            // 5 picks
            [-1, 1],
            [0, 1],
            [1, 1],
            [-0.5, 0],
            [0.5, 0],
        ].map(pos => mapTurnipOffset(pos, -35, 98, 66, 25, 12)),
        specialUpgradeSfx: true,
    },
    {
        requiredStars: 55,
        nodeChestStarsScale: [0.68, 0.68],
        nodeChestStarsAnchors: [0.53, 0.46, 0.53, 0.46],
        futureChestScale: [0.43, 0.43],
        backFrontOffset: [7, -183],
        luckyTurnipPicks: [
            // 10 picks
            [-1.5, 1],
            [-0.5, 1],
            [0.5, 1],
            [1.5, 1],
            [-1.5, 0.5],
            [-0.5, 0.5],
            [0.5, 0.5],
            [1.5, 0.5],
            [-1, 0],
            [0.75, 0],
        ].map(pos => mapTurnipOffset(pos, -35, 118, 50, 25, 12)),
        specialUpgradeSfx: true,
    },
];

/**
 * @param relativePosition abstract numbers from 0 to 1
 * @param x offset from background part of the chest asset
 * @param y offset from background part of the chest asset
 * @param stepX multiplier to the relative position
 * @param stepY multiplier to the relative position
 * @param tilt multiplier to the relative position to add to the vertical axis
 */
function mapTurnipOffset(
    relativePosition: ReadonlyArray<number>,
    x: number,
    y: number,
    stepX: number = 0,
    stepY: number = 0,
    tilt: number = 0
): tVector2 {
    const dx = relativePosition[0] * stepX;
    const dy = relativePosition[1] * stepY + relativePosition[0] * tilt;
    return [x + dx, y + dy];
}

export const STAR_CHEST_SPAWN_INCREMENT: number = 20;

export interface ILuckyTurnipLoot {
    readonly amount: number;
    readonly rewardType: RewardType;
    /**
     * If set, the `amount` will be multiplied
     * by the entry cost of highest level unlocked and by this factor
     */
    readonly levelCostFactor?: number;

    readonly isHighReward: boolean;
}

export const luckyTurnipsLoot: ReadonlyArray<ILuckyTurnipLoot> = [
    {
        amount: 1,
        rewardType: RewardType.COINS,
        levelCostFactor: 1,
        isHighReward: false,
    },
    {
        amount: 1,
        rewardType: RewardType.COINS,
        levelCostFactor: 1.5,
        isHighReward: false,
    },
    {
        amount: 1,
        rewardType: RewardType.COINS,
        levelCostFactor: 2,
        isHighReward: false,
    },
    {
        amount: 1,
        rewardType: RewardType.COINS,
        levelCostFactor: 2.5,
        isHighReward: false,
    },
    {
        amount: 1,
        rewardType: RewardType.COINS,
        levelCostFactor: 3.5,
        isHighReward: false,
    },
    {
        amount: 1,
        rewardType: RewardType.COINS,
        levelCostFactor: 9,
        isHighReward: true,
    },
    { amount: 3, rewardType: RewardType.FREE_PLAY, isHighReward: true },
    { amount: 2, rewardType: RewardType.FREE_PLAY, isHighReward: false },
    { amount: 1, rewardType: RewardType.FREE_PLAY, isHighReward: false },
    { amount: 3, rewardType: RewardType.PLUS_FIVE_CARDS, isHighReward: true },
    { amount: 2, rewardType: RewardType.PLUS_FIVE_CARDS, isHighReward: false },
    { amount: 1, rewardType: RewardType.PLUS_FIVE_CARDS, isHighReward: false },
    { amount: 2, rewardType: RewardType.JOKER, isHighReward: false },
    { amount: 1, rewardType: RewardType.JOKER, isHighReward: false },
    { amount: 3, rewardType: RewardType.UNDO, isHighReward: true },
];
